import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  connectADB,
  detectHardwareModel,
  disconnectADB,
  getApkVersion,
  isAdbConnected,
  onAdbSessionLost,
  getSerial,
} from "@utils/webusb-utils/ADBUtils";
import { adbErrorText } from "@utils/webusb-utils/adbErrors";

export interface AdbConnection {
  connected: boolean;
  connecting: boolean;
  model: string;
  serial: string;
  appVersion: string;
  logs: string[];
  connect: () => Promise<void>;
  log: (message: string) => void;
}

const AdbConnectionContext = createContext<AdbConnection | null>(null);

export const useAdbConnection = (): AdbConnection => {
  const connection = useContext(AdbConnectionContext);
  if (!connection) {
    throw new Error("useAdbConnection must be used inside an AdbConnectionProvider");
  }
  return connection;
};

// For tools that work standalone but join a page-level connection when one exists.
export const useOptionalAdbConnection = (): AdbConnection | null =>
  useContext(AdbConnectionContext);

// `connectADB` keeps a single module-level handle, so every tool talks to the same headset.
export const AdbConnectionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [logs, setLogs] = useState<string[]>([]);

  const log = useCallback((message: string) => setLogs((current) => [...current, message]), []);

  const readDeviceIdentity = useCallback(async () => {
    setModel(await detectHardwareModel());
    setSerial(getSerial() ?? "");
    setAppVersion(await getApkVersion());
  }, []);

  const forgetDevice = useCallback(() => {
    setConnected(false);
    setModel("");
    setSerial("");
    setAppVersion("");
  }, []);

  // Another tool may already hold the shared handle; adopt it, identity first.
  useEffect(() => {
    if (!isAdbConnected()) return;
    void (async () => {
      try {
        log("Using the existing USB connection...");
        await readDeviceIdentity();
        if (!isAdbConnected()) return;
        setConnected(true);
        log("USB connection ready");
      } catch (error) {
        log("Could not read the connected device: " + adbErrorText(error));
        forgetDevice();
      }
    })();
  }, [readDeviceIdentity, forgetDevice, log]);

  useEffect(() => {
    const usb = (navigator as Navigator & { usb?: EventTarget }).usb;
    if (!usb?.addEventListener) return;
    const handleUsbDisconnect = () => {
      void disconnectADB();
      forgetDevice();
      log("USB device disconnected");
    };
    usb.addEventListener("disconnect", handleUsbDisconnect);
    return () => usb.removeEventListener("disconnect", handleUsbDisconnect);
  }, [forgetDevice, log]);

  useEffect(
    () =>
      onAdbSessionLost((error) => {
        forgetDevice();
        if (error) log("USB connection lost; reconnect the headset once it is back up.");
      }),
    [forgetDevice, log],
  );

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      log("Requesting USB device...");
      // connectADB returns early while the shared handle is set, so reconnecting drops it.
      if (isAdbConnected()) {
        log("Releasing the current device…");
        await disconnectADB();
        forgetDevice();
      }
      await connectADB(log);
      await readDeviceIdentity();
      if (!isAdbConnected()) return;
      setConnected(true);
      log("USB connection ready");
    } catch (error) {
      log("Connect error: " + adbErrorText(error));
      forgetDevice();
    } finally {
      setConnecting(false);
    }
  }, [log, readDeviceIdentity, forgetDevice]);

  const connection = useMemo(
    () => ({ connected, connecting, model, serial, appVersion, logs, connect, log }),
    [connected, connecting, model, serial, appVersion, logs, connect, log],
  );

  return (
    <AdbConnectionContext.Provider value={connection}>{children}</AdbConnectionContext.Provider>
  );
};
